<template>
    <div
        :class="containerClass"
    >
        <template v-if="!isLoading">
            <template v-if="hasLeadingIcon">
                <div class="TextInputUI__leading-icon-container">
                    <slot name="leadingIcon"></slot>
                </div>
            </template>

            <div class="TextInputUI__input-wrap">
                <the-mask
                    v-if="mask !== ''"
                    ref="input"
                    :mask="mask"
                    :value="value"
                    :type="type"
                    :class="inputClass"
                    :placeholder="placeholder"
                    :disabled="disabled"
                    @focus="toggleStatus(true)"
                    @blur="toggleStatus(false)"
                    @input="e => $emit('input', e)"
                    @change="e => $emit('change', e)"
                    @keypress.enter="$emit('submit')"
                />
                <input
                    v-else
                    ref="input"
                    :value="value"
                    :type="type"
                    :class="inputClass"
                    :placeholder="placeholder"
                    :disabled="disabled"
                    @focus="toggleStatus(true)"
                    @blur="toggleStatus(false)"
                    @input="e => $emit('input', e.target.value)"
                    @change="e => $emit('change', e.target.value)"
                    @keypress.enter="$emit('submit')"
                >

                <div
                    v-if="!hideStatus && !isStandby"
                    class="TextInputUI__status-icon-container"
                >
                    <slot name="statusIcon">
                        <RPTooltip
                            :tooltip="statusMessage"
                            :icon="statusIconFormatted"
                            :size="16"
                            :fill="statusIconColor"
                            class="TextInputUI__status-icon"
                            stroke="#FFFFFF"
                        />
                    </slot>
                </div>
            </div>

            <span
                v-if="!hideStatus && isError && showErrorMessage"
                class="TextInputUI__error-message"
                v-text="statusMessage"
            ></span>
        </template>
    </div>
</template>
<script>
import stat from 'status/index';
import statusBuilder from 'status/statusBuilder';
import {CheckCircle1} from "@reviewpush/streamline-bold/lib/interface-essential/FormValidation";
import {Delete1} from "@reviewpush/streamline-regular/lib/interface-essential/Delete";
import {AlertTriangle} from "@reviewpush/streamline-bold/lib/interface-essential/Alerts";
import RPTooltip from 'components/Basic/Misc/RPTooltip.vue';
import {TheMask} from 'vue-the-mask';
import {success, error, warning, textDark} from 'style/_colors.scss';

export default {
    name: 'RPTextInputUI',
    components: {
        RPTooltip,
        TheMask
    },
    props: {
        value: {
            type: String,
            required: true
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        placeholder: {
            type: String,
            default: '',
        },
        mask: {
            type: [String, Array],
            default: ''
        },
        status: {
            type: String,
            default: stat.STANDBY,
        },
        statusIcon: {
            type: [Object, Array],
            default: () => ({})
        },
        statusMessage: {
            type: String,
            default: '',
        },
        /**
         * Override the input type
         */
        type: {
            type: String,
            default: 'text'
        },
        showErrorMessage: {
            type: Boolean,
            default: false
        }
    },
    data(){
        return {
            hideStatus: false,
        };
    },
    computed: {
        isStandby(){
            return this.status === stat.STANDBY;
        },
        isLoading(){
            return this.status === stat.LOADING;
        },
        isError(){
            return this.status === stat.ERROR;
        },
        internalStatus(){
            return this.hideStatus
                ? stat.STANDBY
                : this.status;
        },
        hasLeadingIcon(){
            return !!this.$slots.leadingIcon;
        },
        statusIconFormatted(){
            switch(this.internalStatus){
            case stat.WARNING:
                return AlertTriangle;
            case stat.SUCCESS:
                return CheckCircle1;
            case stat.ERROR:
                return Delete1;
            default:
                return this.statusIcon;
            }
        },
        statusIconColor(){
            switch(this.internalStatus){
            case stat.WARNING:
                return warning;
            case stat.SUCCESS:
                return success;
            case stat.ERROR:
                return error;
            default:
                return textDark;
            }
        },
        containerClass(){
            return statusBuilder(
                'TextInputUI',
                this.internalStatus,
                this.disabled
            );
        },
        inputClass(){
            return statusBuilder(
                'TextInputUI__input',
                this.internalStatus,
                this.disabled,
                {
                    indent: this.hasLeadingIcon
                }
            );
        },
    },
    methods: {
        toggleStatus(bool){
            this.hideStatus = bool;
        },
        focus(){
            this.$refs.input.focus();
        },
    }
};
</script>
<style lang="scss">
    .TextInputUI {
        min-width: 10rem;
        position: relative;

        @include loading(2rem, 100%);

        // This is needed to keep the error message from messing with
        // the absolute positioned elements.
        &__input-wrap {
            position: relative;
        }

        &__input {
            font-family: proxima-nova, sans-serif;
            display: block;
            width: 100%;
            height: 2em;
            font-size: 1em;
            padding: 0.5rem;
            border: 1px solid #D5DBE5;
            box-sizing: border-box;
            border-radius: 4px;
            color: $grey-800;

            &::placeholder {
                color: $grey;
                opacity: 0.6;
            }
            &:focus {
                outline: none;
                border: 1px solid $primary-300;
                box-sizing: border-box;
                border-radius: 4px;
            }
            &:disabled {
                background: $grey-100;
                border: 1px solid $grey-100;
                &::placeholder {
                    color: $gradient-dark-blue;
                }
            }

            &--indent {
                padding-left: 1.75rem;
                &::placeholder {
                    padding-left: 1rem;
                }
            }
            &--success {
                border-color: $success-200;
                background: $success-50;
            }
            &--error {
                border-color: $error-200;
                background: $error-50;
            }
            &--warning {
                border-color: $warning-200;
                background: $warning-50;
            }
        }

        &__error-message {
            display: block;
            color: $error-200;
            margin-top: .25rem;
            font-size: .875rem;
        }

        &__status-icon-container,
        &__leading-icon-container {
            position:absolute;
            top: 0;
            bottom: 0;
            display:inline-block;
            width: 1rem;
            height: 1rem;
            margin-top: auto;
            margin-bottom: auto;
        }

        &__leading-icon-container {
            left: 0.5rem;
        }

        &__status-icon-container {
            position:absolute;
            top: 0;
            bottom: 0;
            right: .5rem;
        }
    }
</style>
